import type { UsePriceFormatter } from "@@/translations/use-price-formatter";
import { usePriceFormatter } from "@@/translations/use-price-formatter";
import { useTranslate } from "@@/translations/use-translate";
import type {
    DateRange,
    IsoAndUnixTimestamp,
    Price,
    Timezone,
    Translatable,
    TranslatableType,
    TranslatableValue,
    TranslatableValueFactoryParams,
} from "@towni/common";
import {
    appendTranslatable,
    areTranslatablesEqual,
    asDate,
    hasProp,
    isIsoAndUnix,
    isoAndUnixFactory,
    isPrice,
    isPriceEqual,
    isTranslatable,
    translatableValueFactory,
} from "@towni/common";
import { isDate, isEqual, isThisYear } from "date-fns";
import { memo } from "react";
import { cn } from "../utils/cn";
import type { DateFormatterSelected, DateTimeFormat } from "./date-text-box";
import {
    useDateFormatterSelector,
    useDateRangeFormatted,
} from "./date-text-box";
import type { PriceFractions } from "./price-text-box";

const nmbFormat = new Intl.NumberFormat(undefined, { useGrouping: true });
const format = nmbFormat.format.bind(nmbFormat);

type SpanPrice =
    | Price
    | {
          price: Price | null | undefined;
          whenZero?: NonNullable<TranslatableType>;
          valueToShow?:
              | "AMOUNT_INCLUDING_VAT"
              | "AMOUNT_EXCLUDING_VAT"
              | "VAT_AMOUNT";
          fractions?: PriceFractions;
      };

const isSpanPrice = (input: SpanContent): input is SpanPrice => {
    if (isPrice(input)) return true;
    if (hasProp(input, "price")) return true;
    return false;
};

/**
 * Renders a formatted price span based on the provided price object and formatting options.
 *
 * @param price - The price object or configuration for rendering the price. Can be a `SpanPrice` or an object containing price options.
 * @param format - A formatting function that takes the price, value to show, and fraction options, and returns a translatable type.
 * @returns The formatted price as a `TranslatableType`, a placeholder string ("...") if the price is pending, or the `whenZero` value if the price is zero and `whenZero` is provided.
 */
const renderSpanPrice = (
    price: SpanPrice,
    format: UsePriceFormatter,
): TranslatableType => {
    // Read options and set default values if not provided
    const {
        price: _price,
        whenZero,
        valueToShow = "AMOUNT_INCLUDING_VAT",
        fractions = "AUTO",
    } = isPrice(price)
        ? {
              price,
              whenZero: undefined,
              valueToShow: undefined,
              fractions: undefined,
          }
        : price;

    if (_price === null) return null;
    if (typeof _price === "undefined") {
        // whilst price is undefined it's pending and
        // we'll just show a placeholder
        return "...";
    }

    // Check if price is zero and if so,
    // and whenZero is provided,
    // return whenZero instead
    if (_price.amountIncludingVat === 0 && whenZero) return whenZero;

    return format(_price, valueToShow, fractions);
};

type SpanOptions = {
    doNotFormatNumbers?: boolean;
    ignoreFallbackLanguage?: boolean;
    dateOptions?: {
        timezone?: Timezone;
        /** defaults to */
        dateTimeFormat?: DateTimeFormat;
        hideYear?: boolean;
        forceIncludeYear?: boolean;
    };
};

type _PrimitiveSpanContent =
    | number
    | TranslatableType
    | SpanPrice
    | IsoAndUnixTimestamp
    | Date;

type SpanContent =
    | _PrimitiveSpanContent
    | undefined
    | (_PrimitiveSpanContent | undefined)[];

/**
 * Joins a `SpanContent` input into a single translatable value, optionally formatting numbers,
 * handling price spans, and joining array elements with a separator.
 *
 * @param params - The parameters for joining the span content.
 * @param params.input - The content to join, which can be a string, number, price span, or an array of these.
 * @param params.separator - Optional string to separate joined elements. Defaults to an empty string.
 * @param params.options - Optional configuration for formatting and language handling.
 * @param params.options.doNotFormatNumbers - If true, numbers are not formatted and are converted to strings directly.
 * @param params.options.ignoreFallbackLanguage - If true, ignores fallback language logic.
 * @param params.priceFormatter - A function or object used to format price spans.
 * @returns A `Translatable` or `TranslatableValueFactoryParams` representing the joined and formatted content.
 */
const join = (params: {
    input: SpanContent;
    separator?: string;
    options?: SpanOptions;
    priceFormatter: UsePriceFormatter;
    dateTimeFormatter: DateFormatterSelected;
}): Translatable | TranslatableValueFactoryParams => {
    const {
        input,
        separator = "",
        options,
        dateTimeFormatter,
        priceFormatter,
    } = params;
    const dateOptions = params.options?.dateOptions;
    const getValue = (input: SpanContent): TranslatableType => {
        if (typeof input === "undefined") return null;
        if (input === null) return null;
        if (typeof input === "number") {
            return options?.doNotFormatNumbers ? `${input}` : format(input);
        }
        if (!input) return null;
        if (isSpanPrice(input)) return renderSpanPrice(input, priceFormatter);
        if (isIsoAndUnix(input) || isDate(input))
            return dateTimeFormatter(isoAndUnixFactory(input), {
                includeYear:
                    !dateOptions?.hideYear &&
                    (!!dateOptions?.forceIncludeYear ||
                        !isThisYear(asDate(input))),
            });
        if (!Array.isArray(input)) return input;

        // if input is an empty array, return null
        if (input.length === 0) return null;

        // if input is an array, loop through all items
        // and append them to the result
        let _result: TranslatableValue = {
            _type: "TRANSLATABLE_VALUE",
            sv: "",
        };
        for (const item of input) {
            const value = getValue(item);
            if (typeof value === "undefined") continue;
            if (value === null) continue;
            const _value = isTranslatable(value)
                ? value
                : translatableValueFactory.from({
                      ...value,
                  });
            _result = appendTranslatable(_result, _value, separator, options);
        }
        return _result;
    };

    return getValue(input);
};

type Props = {
    readonly className?: string;
    readonly dataTestId?: string;
    readonly text: SpanContent;
    readonly children?: never;
    readonly options?: SpanOptions;
};

const _Span = (props: Props) => {
    const translate = useTranslate();
    const priceFormatter = usePriceFormatter();
    const dateTimeFormatter = useDateFormatterSelector(
        props.options?.dateOptions?.dateTimeFormat ?? "medium",
    );
    const text = translate(
        join({
            input: props.text,
            priceFormatter,
            dateTimeFormatter,
            options: props.options,
        }),
    );

    return (
        <span
            data-testid={props.dataTestId}
            className={cn("whitespace-pre-wrap", props.className)}>
            {text}
        </span>
    );
};

const arePropsEqual = (prevProps: Props, nextProps: Props): boolean => {
    if (prevProps.className !== nextProps.className) return false;
    if (prevProps.text === nextProps.text) return true;

    // if one is an array and the other is not, return false
    // or if one is a string and the other is not, return false, etc..
    const prevText = prevProps.text;
    const nextText = nextProps.text;

    // if types do not match, return false
    // for example, if one is a string and the other is an array
    if (typeof prevText !== typeof nextText) return false;
    if (typeof prevText === "undefined") return true;
    if (prevText === null) return true;

    if (Array.isArray(prevText) && Array.isArray(nextText)) {
        // if one array is longer than the other, return false
        if (prevText.length !== nextText.length) return false;
        // is the content of the arrays equal, check one by one
        // loop through all items and check if they are equal
        const equal = prevText.every((item, index) => {
            const nextItem = nextText[index];
            if (isPrice(item)) {
                if (!isPrice(nextItem)) return false;
                return isPriceEqual(item, nextItem);
            }
            if (isDate(item)) {
                if (!isDate(nextItem)) return false;
                return isEqual(item, nextItem);
            }
            if (isIsoAndUnix(item)) {
                if (!isIsoAndUnix(nextItem)) return false;
                return item.iso === nextItem.iso;
            }
            if (hasProp(item, "price")) {
                if (!hasProp(nextItem, "price")) return false;
                if (!isPriceEqual(item.price, nextItem.price)) return false;
                if (!areTranslatablesEqual(item.whenZero, nextItem.whenZero))
                    return false;
                if (item.valueToShow !== nextItem.valueToShow) return false;
                if (item.fractions !== nextItem.fractions) return false;
                return true;
            }
            const _item = typeof item === "number" ? `${item}` : item;
            const _nextItem =
                typeof nextItem === "number" ? `${nextItem}` : nextItem;
            if (!isTranslatable(_nextItem)) return false;
            return areTranslatablesEqual(_item, _nextItem);
        });
        return equal;
    }

    // since we know that both are not arrays,
    // we can check if they are equal and force typescript
    // to treat them as TranslatableType
    return areTranslatablesEqual(
        prevText as TranslatableType,
        nextText as TranslatableType,
    );
};

const Span = memo(_Span, arePropsEqual);

export { Span };

const _Span_DateRange = (props: {
    readonly className?: string;
    readonly dateRange: DateRange | undefined;
    /**
     * _Exempel_ \
     * compact: `ÅÅ-MM-DD` \
     * short: `ÅÅÅÅ-MM-DD` \
     * medium: `11:e mars` eller `11 mars 2020` \
     * long: `Måndag den 11:e mars` eller `Måndag den 11 mars 2020` \
     * _defaults to type `medium`
     */
    readonly format?: "compact" | "short" | "medium" | "long";
    readonly children?: never;
    /**
     * _defaults to `false` \
     * Can't be true together with `hideYear`
     * @type {boolean}
     */
    readonly forceIncludeYear?: boolean;
    /**
     * Can't be true together with `forceIncludeYear`
     * @type {boolean}
     */
    readonly hideYear?: boolean;
    /** Defaults to `-` */
    readonly whenUndefined?: Translatable;
}) => {
    const { forceIncludeYear, hideYear, whenUndefined, format, dateRange } =
        props;

    const formattedDateRange = useDateRangeFormatted(dateRange, {
        format: format ?? "medium",
        forceIncludeYear,
        hideYear,
        whenUndefined,
    });

    return <Span text={formattedDateRange} className={props.className} />;
};

const Span_DateRange_DO_NOT_USE__WIP = memo(_Span_DateRange);

export {
    join as joinSpanContent,
    renderSpanPrice,
    Span_DateRange_DO_NOT_USE__WIP,
};
export type { SpanPrice, Props as SpanProps };
